import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

const ProSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role !== 'customer';

  const [pro, setPro] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = async () => {
    const q = pro.trim();
    if (!q) return toast.error('Enter a pro number');
    setLoading(true);
    try {
      const res = await API.get(`/invoices/pro/${encodeURIComponent(q)}`);
      setResults(res.data);
      if (res.data.length === 0) toast('No shipments found for that pro number');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Search failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') runSearch(); };

  const rowLabel = { fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' };
  const rowVal = { fontSize: '14px', fontWeight: '600' };

  const breakdown = (s) => ([
    ['Base', s.base_charge], ['Fuel', s.fuel_surcharge], ['Liftgate', s.liftgate_fee],
    ['Residential', s.residential_charge], ['Two-Man', s.two_man_charge],
    ['Inside', s.inside_charge_amt], ['Unpack', s.unpack_charge_amt],
    ['Misc', s.misc_charges], ['Special', s.special_charges]
  ]);

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '4px' }}>Pro Search</h1>
      <p style={{ color: '#5B6B7F', fontSize: '14px', marginBottom: '20px' }}>
        Look up a shipment by its pro number to see its cost. {isStaff ? 'Every matching instance is shown.' : 'Shows your shipments only.'}
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
        <input
          value={pro}
          onChange={e => setPro(e.target.value)}
          onKeyDown={onKey}
          placeholder="Enter pro number, e.g. PRO-001"
          style={{ flex: 1, maxWidth: '360px', padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '15px', outline: 'none' }}
        />
        <button
          onClick={runSearch}
          disabled={loading}
          style={{ background: '#1A2A3A', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 22px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '500' }}
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {results !== null && (
        <div>
          <p style={{ fontSize: '13px', color: '#5B6B7F', marginBottom: '12px' }}>
            {results.length === 0
              ? 'No shipments found.'
              : `${results.length} shipment${results.length === 1 ? '' : 's'} found${results.length > 1 ? ' (duplicate pro numbers are allowed)' : ''}.`}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {results.map(s => (
              <div key={s.shipment_id} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#F4F7FB', borderBottom: '1px solid #E2E8F0', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
                    <div><p style={rowLabel}>Pro #</p><p style={rowVal}>{s.pro_number}</p></div>
                    <div><p style={rowLabel}>Invoice</p><p style={rowVal}>{s.invoice_number}</p></div>
                    <div><p style={rowLabel}>Date</p><p style={rowVal}>{s.invoice_date ? new Date(s.invoice_date).toLocaleDateString() : '—'}</p></div>
                    {isStaff && <div><p style={rowLabel}>Customer</p><p style={rowVal}>{s.customer_name || '—'}</p></div>}
                    <div><p style={rowLabel}>Type</p><p style={rowVal}>{s.pickup_or_delivery === 'P' ? 'Pickup' : s.pickup_or_delivery === 'D' ? 'Delivery' : '—'}</p></div>
                    <div><p style={rowLabel}>Zone / Bill Wt</p><p style={rowVal}>{s.zone} · {s.billable_weight} lbs</p></div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={rowLabel}>Total</p>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#15803D' }}>{money(s.final_total)}</p>
                  </div>
                </div>

                <div style={{ padding: '14px 18px', display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
                  {breakdown(s).map(([label, val]) => (
                    <div key={label} style={{ minWidth: '78px' }}>
                      <p style={rowLabel}>{label}</p>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: parseFloat(val) > 0 ? '#0F1B2D' : '#9AA6B2' }}>{money(val)}</p>
                    </div>
                  ))}
                </div>

                {isStaff && (
                  <div style={{ padding: '0 18px 14px' }}>
                    <button
                      onClick={() => navigate(`/invoices/${s.invoice_id}`)}
                      style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', color: '#0F1B2D' }}
                    >
                      Open invoice {s.invoice_number} →
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProSearch;
